/**
 * Privacy & Consent — DPDP consent status, self-service data export (right to
 * data portability), shortcuts to the existing Privacy / DP Controls pages,
 * and account-level AI data controls. Shared by all user types.
 *
 * The AI controls save to User.aiPreferences (cross-device defaults) AND sync
 * the live chat consent in ChatContext, so a change here takes effect in the
 * current AI chat immediately — not just on the next device.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useUI } from '../../context/UIContext';
import { ROUTES } from '../../constants/constants';
import { ProfileCard, FieldRow, Pill, GhostButton, PrimaryButton } from './ProfilePrimitives';
import { Select } from '../common/FormControls';

const MUTED = '#7a7060';
const TEXT = '#08111f';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ConsentToggle({ label, description, isEnabled, onToggle, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-[12.5px] font-medium" style={{ color: TEXT }}>{label}</div>
        <div className="text-[11px] mt-[2px]" style={{ color: MUTED }}>{description}</div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors mt-[2px]"
        style={{ backgroundColor: isEnabled ? '#145f5f' : '#ddd6c6', opacity: disabled ? 0.6 : 1 }}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </button>
    </div>
  );
}

const AUTO_DELETE_OPTIONS = [
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
  { value: '36m', label: '36 months (default)' },
  { value: 'forever', label: 'Until I delete them' },
];
const AUTO_DELETE_LABELS = { '90d': '90 days', '1y': '1 year', '36m': '36 months', forever: 'until you delete them' };

export default function PrivacyConsentSection({ user }) {
  const navigate = useNavigate();
  const { showToast } = useUI();
  const { updateProfile } = useAuth();
  const { consentPersist, setConsentPersist, consentTrain, setConsentTrain } = useChat();
  const [exporting, setExporting] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  // Auto-delete picker (retention re-shape): kept locally so the select
  // reflects the save immediately; seeded from the account default.
  const [autoDelete, setAutoDelete] = useState(user?.aiPreferences?.historyAutoDelete || '36m');

  const consentDate = formatDate(user?.consentGivenAt);

  const handleExport = async () => {
    setExporting(true);
    try {
      await authAPI.exportData();
      showToast('Your data export has been downloaded', 'success');
    } catch (err) {
      showToast(err?.message || 'Data export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Save the account default AND flip the live chat consent so the change is
  // effective in this session immediately. The backend merges partial
  // aiPreferences, audits the change (user.ai_consent_changed), and — for the
  // auto-delete picker — recomputes every live chat's expiry on the spot.
  const saveAiPreference = async (patch) => {
    setSavingAi(true);
    const next = {
      saveHistory: consentPersist,
      allowTraining: consentTrain,
      historyAutoDelete: autoDelete,
      ...patch,
    };
    try {
      await updateProfile({ aiPreferences: next });
      if ('saveHistory' in patch) setConsentPersist(patch.saveHistory);
      if ('allowTraining' in patch) setConsentTrain(patch.allowTraining);
      if ('historyAutoDelete' in patch) setAutoDelete(patch.historyAutoDelete);
      showToast('AI preference saved', 'success');
    } catch (err) {
      showToast(err?.message || 'Could not save AI preference', 'error');
    } finally {
      setSavingAi(false);
    }
  };

  return (
    <div className="space-y-5">
      <ProfileCard
        title="Privacy & Consent"
        subtitle="Manage your consent and download a copy of your data (DPDP Act 2023)."
        icon="🔒"
        action={
          <PrimaryButton onClick={handleExport} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download my data'}
          </PrimaryButton>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>
              Consent status
            </div>
            {consentDate ? (
              <Pill tone="green">✓ Consent given {consentDate}</Pill>
            ) : (
              <Pill tone="orange">No consent recorded</Pill>
            )}
          </div>
          <FieldRow label="Consent version" value={user?.consentVersion} />
        </div>

        <div className="mt-5 pt-4 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: '#eee6d8' }}>
          <GhostButton onClick={() => navigate(ROUTES.PRIVACY)}>Data Privacy</GhostButton>
          <GhostButton onClick={() => navigate(ROUTES.DP_CONTROLS)}>DP Controls</GhostButton>
        </div>
      </ProfileCard>

      <ProfileCard
        title="AI & your data"
        subtitle="How Vee (our AI) handles your conversations. Changes apply to your current chat immediately."
        icon="ai"
      >
        <div className="space-y-5">
          <ConsentToggle
            label="Save my AI chat history"
            description={`Keep conversations so you can reopen them later (kept ${AUTO_DELETE_LABELS[autoDelete] || '36 months'}, deletable anytime). Off = private mode: nothing is stored after the reply.`}
            isEnabled={consentPersist}
            onToggle={() => saveAiPreference({ saveHistory: !consentPersist })}
            disabled={savingAi}
          />

          {consentPersist && (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[12.5px] font-medium" style={{ color: TEXT }}>Auto-delete chat history</div>
                <div className="text-[11px] mt-[2px]" style={{ color: MUTED }}>
                  Applies to existing chats immediately. Deleted chats stay recoverable for 7 days, then purge fully within 30.
                </div>
              </div>
              <div className="shrink-0 w-[168px]">
                <Select
                  size="sm"
                  value={autoDelete}
                  disabled={savingAi}
                  onChange={(v) => saveAiPreference({ historyAutoDelete: v })}
                  options={AUTO_DELETE_OPTIONS}
                />
              </div>
            </div>
          )}

          <ConsentToggle
            label="Use my data to improve Vee"
            description="We use your saved chats and drafts — with identifiers removed — to make Vee better. Nothing leaves our systems, and this never changes how long your chats are kept. Improvement data is retained up to 3 years and removed from future use if you switch this off."
            isEnabled={consentTrain}
            onToggle={() => saveAiPreference({ allowTraining: !consentTrain })}
            disabled={savingAi}
          />
          <div className="text-[10.5px] pt-1" style={{ color: MUTED }}>
            You can also change these from the privacy control inside the AI chat. Deleting a
            conversation always removes it regardless of these settings.
          </div>
        </div>
      </ProfileCard>

      {/* D-4 — the honest receipt: exactly what improves Vee and on what terms. */}
      <ProfileCard
        title="How your data improves Vee"
        subtitle="Plain answers, no legalese."
        icon="review"
      >
        <div className="space-y-3 text-[12px] leading-relaxed" style={{ color: TEXT }}>
          <div>
            <span className="font-semibold">Always (no content):</span>{' '}
            we count how Vee performs — which tools ran, how often drafts failed or fell back,
            speed, and token usage. Numbers only; never your conversations or documents.
          </div>
          <div>
            <span className="font-semibold">Only with “Use my data to improve Vee” on:</span>{' '}
            we keep two kinds of examples — a draft you hand-corrected (your before/after edit),
            and turns where Vee failed. Identifiers (CIN, DIN, PAN, email, phone, GSTIN) are
            removed <em>before</em> anything is stored.
          </div>
          <div>
            <span className="font-semibold">When you rate a reply (thumbs up/down):</span>{' '}
            that one exchange is sent with your rating — the buttons say so — identifiers removed
            the same way.
          </div>
          <div className="text-[11px] pt-1 border-t" style={{ color: MUTED, borderColor: '#eee6d8' }}>
            Improvement data lives at most 3 years, never leaves our systems, is included in your
            data export, and is deleted when you switch the toggle off (rating feedback goes when
            you delete your chat history). Chat retention is separate — it follows your auto-delete
            choice above, never this toggle.
          </div>
        </div>
      </ProfileCard>
    </div>
  );
}
